import { cn } from '@utils/cn';
import { Text, Input, HelperText } from '@ui-component-library';
import type { InputType } from '@ui-component-library/components/Input/Input.types';

type FormSectionProps = {
    title: string;
    children: React.ReactNode;
    headerAction?: React.ReactNode;
    className?: string;
};

type FormSectionFieldProps = {
    name: string;
    label: string;
    type?: InputType;
    value: string;
    error?: string;
    onChange: (val: string | number) => void;
    onClearError: () => void;
    show?: boolean;
    /** Emphasize this field (e.g. obligatoriska fält från `?required_fields=`). */
    highlight?: boolean;
};

function FormSection({ title, children, headerAction, className = '' }: FormSectionProps) {
    return (
        <section className={`rounded-2xl bg-white-50 border-solid border-1 border-cloud-500 p-4 ${className}`.trim()}>
            <div className={headerAction ? 'flex justify-between items-center mb-4' : ''}>
                <Text variant="text-base/leading-normal/medium" className={headerAction ? 'text-charcoal-1000' : 'text-charcoal-1000 mb-4'}>
                    {title}
                </Text>
                {headerAction}
            </div>
            <div className="space-y-2">
                {children}
            </div>
        </section>
    );
}

FormSection.Grid = function FormSectionGrid({ children }: { children: React.ReactNode }) {
    return <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>;
};

FormSection.Field = function FormSectionField({
    name,
    label,
    type = 'text',
    value,
    error,
    onChange,
    onClearError,
    show = true,
    highlight = false,
}: FormSectionFieldProps) {
    if (!show) return null;
    return (
        <div>
            <Input
                variant="fluid"
                name={name}
                label={label + (highlight ? "*" : "")}
                type={type}
                value={value}
                isValid={error ? false : undefined}
                onFocus={onClearError}
                onChange={(val) => onChange(val)}
                debounceDelay={0}
                className={cn(
                    'w-full',
                    highlight && !error && '!border-warning-500'
                )}
            />
            {error && (
                <HelperText
                    icon="ExclamationFilled"
                    label={error}
                    className="text-danger-500 fill-danger-500 mt-2"
                />
            )}
        </div>
    );
};

export default FormSection;